import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { getProductById } from "../data/products";
import Coin from "../components/Coin";
import { toast } from "sonner";
import sfx from "../utils/sound";
import AdBanner from "../components/AdBanner";

const Cart = () => {
  const { cart, updateQty, removeFromCart, coins, clearCart } = useStore();
  const nav = useNavigate();

  const items = cart
    .map((c) => {
      const p = getProductById(c.id);
      return p ? { ...p, qty: c.qty } : null;
    })
    .filter(Boolean);

  const total = items.reduce((sum, it) => sum + it.price * it.qty, 0);
  const canAfford = coins >= total && items.length > 0;

  const goCheckout = () => {
    if (!canAfford) {
      if (items.length === 0) return;
      sfx.lose();
      toast("Not enough coins! Try removing something.", { duration: 1800 });
      return;
    }
    sfx.success();
    nav("/checkout");
  };

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-8" data-testid="cart-page">
      <Link
        to="/shop"
        className="btn-3d btn-sm btn-ghost mb-4"
        data-testid="cart-back-btn"
      >
        <ArrowLeft size={18} strokeWidth={3} /> Keep shopping
      </Link>

      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-3xl sm:text-4xl">
          <span className="wiggle inline-block mr-2">🛒</span>Your cart
        </h1>
        {items.length > 0 && (
          <button
            className="btn-3d btn-sm btn-ghost"
            onClick={() => {
              clearCart();
              toast("Cart emptied!", { duration: 1400 });
            }}
            data-testid="cart-clear-btn"
          >
            <Trash2 size={16} strokeWidth={3} /> Empty
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div
          className="chunky-card p-10 text-center"
          data-testid="cart-empty-state"
        >
          <div className="text-7xl mb-4 bob">🎈</div>
          <h2 className="font-display text-2xl mb-2">Your cart is squeaky clean</h2>
          <p className="text-lg mb-5" style={{ color: "#484848" }}>
            Go pile it up with joy!
          </p>
          <Link to="/shop" className="btn-3d" data-testid="cart-go-shop-btn">
            <ShoppingBag size={20} strokeWidth={3} /> Start shopping
          </Link>
        </div>
      ) : (
        <>
          <ul className="flex flex-col gap-4" data-testid="cart-items-list">
            {items.map((it) => (
              <li
                key={it.id}
                className="chunky-card p-4 flex items-center gap-4"
                data-testid={`cart-item-${it.id}`}
              >
                <div
                  className="rounded-2xl flex items-center justify-center shrink-0"
                  style={{
                    width: 72,
                    height: 72,
                    background: it.color + "33",
                    border: "3px solid #2b2b2b",
                  }}
                  aria-hidden="true"
                >
                  <span style={{ fontSize: 42 }}>{it.emoji}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/product/${it.id}`}
                    className="font-display text-lg leading-tight block"
                    data-testid={`cart-item-name-${it.id}`}
                  >
                    {it.name}
                  </Link>
                  <div
                    className="text-sm mt-1 flex items-center gap-1"
                    style={{ color: "#484848" }}
                  >
                    <Coin size={16} /> {it.price} each
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-4">
                  <div
                    className="inline-flex items-center rounded-full overflow-hidden"
                    style={{
                      border: "3px solid #2b2b2b",
                      boxShadow: "0 3px 0 #2b2b2b",
                    }}
                  >
                    <button
                      className="p-2"
                      onClick={() => updateQty(it.id, it.qty - 1)}
                      aria-label="Decrease"
                      data-testid={`cart-qty-dec-${it.id}`}
                      style={{ background: "#FFF9E6" }}
                    >
                      <Minus size={16} strokeWidth={3} />
                    </button>
                    <span
                      className="px-3 font-display text-base"
                      data-testid={`cart-qty-${it.id}`}
                      style={{ background: "#fff", minWidth: 36, textAlign: "center" }}
                    >
                      {it.qty}
                    </span>
                    <button
                      className="p-2"
                      onClick={() => updateQty(it.id, it.qty + 1)}
                      aria-label="Increase"
                      data-testid={`cart-qty-inc-${it.id}`}
                      style={{ background: "#FFF9E6" }}
                    >
                      <Plus size={16} strokeWidth={3} />
                    </button>
                  </div>
                  <div
                    className="coin-pill text-sm"
                    data-testid={`cart-line-total-${it.id}`}
                  >
                    <Coin size={16} /> {it.price * it.qty}
                  </div>
                  <button
                    className="rounded-full flex items-center justify-center"
                    onClick={() => removeFromCart(it.id)}
                    aria-label="Remove item"
                    data-testid={`cart-remove-${it.id}`}
                    style={{
                      width: 40,
                      height: 40,
                      background: "#fff",
                      border: "3px solid #FF3B30",
                      color: "#FF3B30",
                      boxShadow: "0 3px 0 #FF3B30",
                    }}
                  >
                    <Trash2 size={18} strokeWidth={3} />
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <div
            className="chunky-card p-5 sm:p-6 mt-6 flex flex-col sm:flex-row items-center gap-4 sm:justify-between"
            data-testid="cart-summary"
          >
            <div>
              <div className="font-display text-2xl" data-testid="cart-total-label">
                Total: {total} coins
              </div>
              <div className="text-sm" style={{ color: coins < total ? "#FF3B30" : "#484848" }}>
                You have {coins} coins in your wallet
              </div>
            </div>
            <button
              className="btn-3d btn-accent"
              style={{ fontSize: 20 }}
              disabled={!canAfford}
              onClick={goCheckout}
              data-testid="cart-checkout-btn"
            >
              🎉 Checkout
            </button>
          </div>
          {/* Sponsored — cart footer ad slot. */}
          <div className="mt-4" data-testid="cart-ad-slot">
            <AdBanner slot="cart_footer" />
          </div>
        </>
      )}
    </div>
  );
};

export default Cart;
